/* eslint-disable no-shadow */
import _ from "underscore";
import chroma from "chroma-js";

import vars from "@exo/narrative-tracker-variables";
import { setFieldById } from "@exo/helpers";

// ---- Arrays of color objects ----
// colorList
// colors

// ---- Functions ----
// assignColor
// removeColor
// createMapKey
// addColorToGraphs
// addColorToNestedArray

// this list is used with the Graph tab, and relies on the colors being in a shuffled order to create separation
export const colors = [
	{ // 0
		id: "Blue",
		dark: "#0074E4",
		light: "#7b4db8", // Purple
		colorblind: "colorblindDashed",
		inUse: 0,
	},
	{ // 1
		id: "Cornflower",
		dark: "#66a4ff",
		light: "#2232e0", // Blue
		colorblind: "colorblindBroken",
		inUse: 0,
	},
	{ // 2
		id: "Uranian_Blue",
		dark: "#b3f3fc", // off white
		light: "#0bbfd6", // Seafoam
		colorblind: false,
		inUse: 0,
	},
	{ // 3
		id: "Seafoam",
		dark: "#1cce99",
		light: "#009e7d", // teal
		colorblind: "colorblindDots",
		inUse: 0,
	},
	{ // 4
		id: "Grass",
		dark: "#32C402",
		light: "#007005", // Green
		colorblind: "colorblindTinyDots",
		inUse: 0,
	},
	{ // 5
		id: "Chartreuse",
		dark: "#C2EB3C",
		light: "#86c419", // Chartreuse
		colorblind: "colorblindStripes",
		inUse: 0,
	},
	{ // 6
		id: "Light_Yellow",
		dark: "#F0FCAC",
		light: "#f7c20a", // Yellow
		colorblind: "colorblindBig",
		inUse: 0,
	},
	{ // 7
		id: "Goldenrod",
		dark: "#CEBF09",
		light: "#f16b20", // Orange
		colorblind: "colorblindSmall",
		inUse: 0,
	},
	{ // 8
		id: "Orange",
		dark: "#ce9309", //
		light: "#c02e1d", // Red
		colorblind: "colorblindGaps",
		inUse: 0,
	},
	{ // 9
		id: "lilac",
		dark: vars.lilac_1,
		light: vars.lilac_1,
		colorblind: "colorblindBig", // contrary color to other colorblindDashed - hopefully this is fine
		inUse: 0,
	},
	{ // 10
		id: "royalBlue",
		dark: "#7b4db8", // Warm purple
		light: vars.purple_2,
		colorblind: "colorblindTiny",
		inUse: 0,
	},
	{ // 11
		id: "persimmon",
		dark: vars.persimmon_1,
		light: vars.plum_1,
		colorblind: "colorblindTiny",
		inUse: 0,
	},

	// {
	// 	name: "Lime",
	// 	dark: "#00CC35",
	// 	light: "#88c400", // Exovera Green
	// 	inUse: 0,
	// },
	// {
	// 	name: "Yellow",
	// 	dark: "#F3D02D",
	// 	light: "#ebc844", // Yellow
	// 	inUse: 0,
	// },
	// {
	// 	name: "Cornflower",
	// 	dark: "#3E9BF7",
	// 	light: "#009fb7", // aquamarine (accent)
	// 	inUse: 0,

	// },
	// {
	// 	name: "Sea Green",
	// 	dark: "#23C7B4",
	// 	light: "#ecaa38", // Orange
	// 	inUse: 0,
	// },
	// {
	// 	name: "Chartreuse",
	// 	dark: "#B2CE09",
	// 	light: "#1f65b2", // Dark Cornflower
	// 	inUse: 0,
	// },

	// dark: "#2e42ff", // royal blue
	// light: "#2e42ff",
	// colorblind: "colorblindTiny",
];

// Return the first instance of an unused color.
//  Adds new keys to track multiple used colors.
//  Clones colorStore, but mutates trend. trend must be mutable!
export const assignColor = (params) => {
	const { trend, colorStore } = params;

	const colorLib = JSON.parse(JSON.stringify(colorStore));

	// Find least used color (will return first lowest color if there are multiple with the lowest score)
	const reducer = (lowestColor, currentColor) => {
		return currentColor.inUse < lowestColor.inUse ? currentColor : lowestColor;
	};
	const newColor = colorLib.reduce(reducer, colorStore[0]);

	// Assign color to the trend
	trend.color = newColor;

	// increment the color in the color store
	const updatedColorStore = setFieldById({
		array: colorLib,
		id: newColor.id,
		key: "inUse",
		value: (newColor.inUse += 1),
	});

	return {
		trend,
		colorStore: updatedColorStore,
	};
};

// returns the list of objects, with first available color from an array of colors. 
// 		Differs from assignColor because it returns the items in order & does not require maintaining a colorStore 
// 		- list: array of objects, some have color objects, some do not. Must have id: string field
//		- colors: array of color objects, some in use, others not.
export const assignStaticColor = (params) => {
	const { list, colors } = params;

	const clonedList = JSON.parse(JSON.stringify(list));
	const clonedColors = JSON.parse(JSON.stringify(colors));

	// Remove used colors 
	const unusedColors = _.reject(clonedColors, (color) => {
		return _.find(clonedList, (obj) => {
			return obj.color && obj.color.id === color.id;
		});
	});

	// Add colors to objects without them
	_.each(clonedList, (obj) => {
		if (!obj.color) {
			obj.color = unusedColors[0];
			unusedColors.shift(); // remove first color
		}
	});

	return clonedList;
}

// Clones colorStore and decrements the removed color.
export const removeColor = (params) => {
	const { color, colorStore } = params;
	const colorLib = JSON.parse(JSON.stringify(colorStore));
	if (color) {
		const lowerColor = _.find(colorLib, (colorInLib) => {
			return colorInLib.id === color.id;
		});
		if (lowerColor) {
			lowerColor.inUse--;
		}
	}
	return colorLib;
};

export const createMapKey = (params) => {
	const { dark, light, levels } = params;

	const sortedLevels = _.sortBy(JSON.parse(JSON.stringify(levels)), "level");

	// Build color scale based on the narrative
	const darkScale = chroma.scale(["#2a3851", dark]).colors(6);
	const lightScale = chroma.scale(["#2a3851", light]).colors(6);

	// Assign color scale to sortedLevels
	for (let index = 0; index < sortedLevels.length; index++) {
		sortedLevels[index].color = {
			// ignore first color, since 0 isn't on the map scale (it's "no data" instead)
			dark: darkScale[index + 1],
			light: lightScale[index + 1],
		};
	}

	return sortedLevels;
};

// ---- Single narrative graph colors -----
const addGraphColorType = (graph) => {
	_.each(graph.data, (type) => {
		if (type.id === "typeIndividuals") {
			type.color = "#DAB83A";
		} else if (type.id === "typeOrgs") {
			type.color = "#c9ce39";
		} else if (type.id === "typeBloggers" || type.id === "typeBots") {
			type.color = "#33D3A4";
		} else if (type.id === "typeJournalists") {
			type.color = "#20B7BE";
		}
	});
	return graph;
};

const addGraphColorPolitical = (graph) => {
	_.each(graph.data, (type) => {
		if (type.id === "politicalLeft") {
			type.color = "#388EFC";
		} else if (type.id === "politicalRight") {
			type.color = "#E25398";
		} else if (type.id === "politicalCenter") {
			type.color = "#BC76E6";
		}
	});
	return graph;
};

const addGraphColorFollowers = (graph) => {
	_.each(graph.data, (type) => {
		if (type.id === "followersLow") {
			type.color = "#35CAFC";
		} else if (type.id === "followersMedLow") {
			type.color = "#33D3A4";
		} else if (type.id === "followersMed") {
			type.color = "#c9ce39";
		} else if (type.id === "followersMedHigh") {
			type.color = "#DAB83A";
		} else if (type.id === "followersHigh") {
			type.color = "#E98664";
		}
	});
	return graph;
};

// Clones colorStore and decrements the removed color.
export const addColorToGraphs = (expandedContentObj, narratives) => {
	const expandedContent = JSON.parse(JSON.stringify(expandedContentObj));

	// add color to the graphs
	_.each(expandedContent.graphs, (graph) => {
		if (graph.id === "type") {
			addGraphColorType(graph);
		} else if (graph.id === "political") {
			addGraphColorPolitical(graph);
		} else if (graph.id === "followers") {
			addGraphColorFollowers(graph);
		}
	});

	// Add narrative colors
	_.each(expandedContent.tags, (tag) => {
		const thisNar = _.find(narratives, (nar) => {
			return nar.id === tag.id;
		});
		tag.color = thisNar && thisNar.color;
	});

	return expandedContent;
};

// This list is used with addColorToNestedArray (Narrative & compare tabs)
// NOTE: colors should be in an ordered scale
export const colorList = [
	{
		// 0
		id: "blue",
		dark: "#0074E4", // blue
		light: "#7b4db8", // Purple
		colorblind: "colorblindDashed",
	},
	{
		// 1
		id: "Cornflower",
		dark: "#66a4ff", // Light Blue
		light: "#2232e0", // "#345ac6", // Blue
		colorblind: "colorblindBroken",
	},
	{
		// 2
		id: "Uranian_Blue",
		dark: "#b3f3fc", // off white
		light: "#0bbfd6", // Seafoam
		colorblind: false,
	},
	{
		// 3
		id: "Seafoam",
		dark: "#1cce99",
		light: "#009e7d", // teal
		colorblind: "colorblindDots",
	},
	{
		// 4
		id: "grass",
		dark: "#32C402", // grass
		light: "#007005", // Green
		colorblind: "colorblindTinyDots",
	},
	{
		// 5
		id: "chartreuse",
		dark: "#c2eb3c", // chartreuse
		light: "#86c419", // Chartreuse
		colorblind: "colorblindStripes",
	},
	{
		// 6
		id: "Light_Yellow",
		dark: "#F0FCAC", // pale green
		light: "#f7c20a", // Yellow
		colorblind: "colorblindBig",
	},
	{
		// 7
		id: "goldenrod",
		dark: "#CEBF09", // Goldenrod
		light: "#f16b20", // Orange
		colorblind: "colorblindSmall",
	},
	{
		// 8
		id: "orange",
		dark: "#ce9309", //
		light: "#c02e1d", // Red
		colorblind: "colorblindGaps",
	},
	{
		// 9
		id: "lilac",
		dark: "#a38dff", // lilac
		light: "#a38dff", // lilac
		colorblind: "colorblindBig",
	},
	{
		// 10
		id: "royalBlue",
		dark: "#7b4db8", // warm purple
		light: "#8e8e00", // Drab
		colorblind: "colorblindTiny",
	},
];

export const altColorList = [
	{
		// 0
		id: "fushia",
		dark: "#b467e5",
		light: "#b467e5",
		colorblind: "colorblindTiny",
	},
	{
		// 1
		id: "lilac",
		dark: "#a38dff",
		light: "#a38dff",
		colorblind: "colorblindDashed",
	},
	{
		// 2
		id: "carolina",
		dark: "#54c4e2",
		light: "#54c4e2",
		colorblind: "colorblindBroken",
	},
	{
		// 3 - // NOTE: This is reused if there are 6 + topics
		id: "chartruese",
		dark: "#c2eb3c",
		light: "#c2eb3c",
		colorblind: false,
	},
	{
		// 4
		id: "gold",
		dark: "#cebf09",
		light: "#cebf09",
		colorblind: "colorblindDots",
	},
];

// Installs the object "color" to each element on the array
// Mutates array, so clone it first if you don't want that
// 	gently: Boolean - Won't override existing colors
const pickColors = (params) => {
	const { array, colorsArray, gently, parentColorIndex } = params;
	// If there aren'e enough colors, parent is ignored
	if (array.length === colorsArray.length) {
		// If there is no parent and the arrays are the same length, assign like normal
		_.each(array, (obj, index) => {
			if (!gently || !obj.color) {
				obj.color = colorsArray[index];
			}
		});
	} else if (array.length > colorsArray.length) {
		// If there aren't enough colors, recyle them (start at 1; 0 is ugly, if you haven't heard)
		// Start at 1 if no parent, otherwise start after the parent, unless it's at the end
		let colorIndex = parentColorIndex < 0 ? 1 : parentColorIndex < colorsArray.length ? parentColorIndex + 1 : 0;
		for (let i = 0; i < array.length; i++) {
			if (!gently || !array[i].color) {
				array[i].color = colorsArray[colorIndex];
				// increment colorsIndex
				if (colorIndex === colorsArray.length - 1) {
					colorIndex = 0;
				} else {
					colorIndex++;
				}
			}
		}
	} else if (parentColorIndex < 0) {
		// No Parent color supplied
		// if there is no parent, start with 1 (0 is ugly), if there is room
		if (array.length < colorsArray.length) {
			_.each(array, (obj, index) => {
				if (!gently || !obj.color) {
					obj.color = colorsArray[index + 1];
				}
			});
		}

		// If there is a parent, don't include it unless you have to
	} else {
		const colorsBeforeParent = colorsArray.slice(0, parentColorIndex);
		const colorsAfterParent = colorsArray.slice(parentColorIndex + 1);
		if (array.length < colorsBeforeParent) {
			_.each(array, (obj, index) => {
				if (!gently || !obj.color) {
					obj.color = colorsBeforeParent[colorsBeforeParent.length - index]; // closest to parent first
				}
			});
		} else if (array.length < colorsAfterParent) {
			_.each(array, (obj, index) => {
				if (!gently || !obj.color) {
					obj.color = colorsAfterParent[index]; // closest to parent first
				}
			});
		} else {
			// If the arrays must be combined
			const uglyColor = colorsBeforeParent.shift();
			const colorLibWithoutParent = [...colorsAfterParent, ...colorsBeforeParent, uglyColor];
			_.each(array, (obj, index) => {
				if (!gently || !obj.color) {
					obj.color = colorLibWithoutParent[index];
				}
			});
		}
	}

	return array;
};

// Assigns the field 'color' to all objects in the array.
//	if childName is passed, it will do this for all objects in that array
//	array: Array of objects.
//	childName: String - must be the key to an Array of objects contained in Array
// 	gently: Boolean - passed to pickColors. Won't override existing colors
//	sortBy: String - the key to sort the arrays & child arrays by before they are colored. Otherwise, the arrays will be colored in the order they arrive
//	startIndex: number
export const addColorToNestedArray = (params) => {
	const { array, childName, sortBy, shallow, altColors, gently } = params;

	if (!array) {
		return false;
	} else {
		const sortedParent = sortBy ? _.sortBy(array, sortBy) : array;
		// assign colors in the 'color' field
		const coloredArray = pickColors({
			array: sortedParent,
			colorsArray: altColors ? altColorList : colorList,
			gently,
			parentColorIndex: -1,
		});

		if (!shallow) {
			// do this for children
			_.each(coloredArray, (obj) => {
				const sortedChild = sortBy ? _.sortBy(obj[childName], sortBy) : obj[childName];
				if (obj[childName]) {
					obj[childName] = pickColors({
						array: sortedChild,
						colorsArray: colorList,
						gently,
						parentColorIndex: _.findIndex(colorList, (color) => {
							return color.id === obj.color.id;
						}),
					});
				}
			});
		}

		return coloredArray;
	}
};

export const leftColors = {
	left1: {
		id: "left1",
		dark: vars.left_1,
		light: vars.left_1,
		colorblind: "colorblindDashed",
		inUse: 0,
	},
	left2: {
		id: "left2",
		dark: vars.left_2,
		light: vars.left_2,
		colorblind: "colorblindBroken",
		inUse: 0,
	},
	left3: {
		id: "left3",
		dark: vars.left_3,
		light: vars.left_6,
		colorblind: false,
		inUse: 0,
	},
	left4: {
		id: "left4",
		dark: vars.blue_0_5,
		light: vars.left_4,
		colorblind: "colorblindDots",
		inUse: 0,
	},
	left5: {
		id: "left5",
		dark: vars.mint_1,
		light: vars.left_5,
		colorblind: "colorblindTinyDots",
		inUse: 0,
	},
	left6: {
		id: "left6",
		dark: vars.left_7,
		light: vars.cerulean_3,
		colorblind: "colorblindTiny",
		inUse: 0,
	},
};

export const rightColors = {
	right1: {
		id: "right1",
		dark: vars.right_1,
		light: vars.right_7,
		colorblind: "colorblindStripes",
		inUse: 0,
	},
	right2: {
		id: "right2",
		dark: vars.right_2,
		light: vars.right_6,
		colorblind: "colorblindBig",
		inUse: 0,
	},
	right3: {
		id: "right3",
		dark: vars.right_4,
		light: vars.right_4,
		colorblind: "colorblindSmall",
		inUse: 0,
	},
	right4: {
		id: "right4",
		dark: vars.persimmon_1,
		light: vars.persimmon_1,
		colorblind: "colorblindGaps",
		inUse: 0,
	},
	right5: {
		id: "right5",
		dark: vars.right_5,
		light: vars.right_5,
		colorblind: "colorblindBig",
		inUse: 0,
	},
	// right6: {
	// 	id: "right5",
	// 	dark: vars.plum_1,
	// 	light: vars.plum_1,
	// 	colorblind: "colorblindTiny",
	// 	inUse: 0,
	// },
};
